import mixpanel from 'mixpanel-browser';
import { useSession } from 'next-auth/react';
import getConfig from 'next/config';
import { useCallback, useEffect, useMemo } from 'react';
import { isProductionEnv } from 'lib/client/utils';
import logger from '@finance/shared/dist/utils/logger';
import { useSegment } from 'providers/SegmentProvider';
import useFullStory from 'lib/analytics/hooks/useFullStory';
import { AnalyticsReporter, ITrackEvents } from '@honeybook/finance-components/dist/hooks/analytics/analytics.types';
import useExternalAnalytics from '@honeybook/finance-components/dist/hooks/analytics/useAnalytics';
import { AnalyticsEventRecord } from './analyticsRegistry';
import { isTesting } from 'components/onboarding/application/ApplicationDocumentAttachment';

export type {
    IAnalyticsEventProperties,
    ITrackEvents,
    AnalyticsEventTypes
} from '@honeybook/finance-components/dist/hooks/analytics/analytics.types';

let delayedEvents: ITrackEvents[] = [];
let isMixpanelLoaded = false;

function releaseMixpanelQueue() {
    let event: ITrackEvents | undefined;

    while ((event = delayedEvents.shift())) {
        logger.info(event, `mixpanel event queued, sending now`);
        mixpanel.track(event.eventName, {
            ...event.properties
        });
    }
}

// Exposed for testing purposes only
export function __resetQueue() {
    delayedEvents = [];
    isMixpanelLoaded = false;
}

export function useGetAnalyticsCb() {
    const { trackFullstoryEvent } = useFullStory();
    const { track: trackSegmentEvent } = useSegment();
    const {
        publicRuntimeConfig: { mixpanelToken, env }
    } = getConfig();
    const shouldTrackEvents = useMemo(() => isProductionEnv() && mixpanelToken, [mixpanelToken]);

    const session = useSession();

    useEffect(() => {
        if (shouldTrackEvents && !isMixpanelLoaded) {
            mixpanel.init(mixpanelToken, {
                loaded: () => {
                    logger.info('mixpanel loaded successfully');
                    isMixpanelLoaded = true;
                    releaseMixpanelQueue();
                }
            });
        }
    }, [shouldTrackEvents, mixpanelToken]);

    useEffect(() => {
        if (isMixpanelLoaded && session.data) {
            logger.debug({ session }, 'identifying mixpanel user');
            mixpanel.identify(session.data.user._id);
            mixpanel.people.set('unitCustomerId', session.data.customerId);
            mixpanel.people.set('email', session.data.user.email);
            mixpanel.register({
                platform: 'Finance app',
                environment: env
            });
        }
    }, [session, env]);

    const logEvent = useCallback((event: { eventName: string; eventType: string; properties: unknown }, appPath?: string) => {
        const { eventName, eventType, properties } = event;
        logger.info(
            { eventName: `${eventType}: ${eventName}`, properties: { app_path: appPath, ...(properties || {}) } },
            'Analytics event'
        );
    }, []);

    return useCallback(
        (event: ITrackEvents, appPath?: string) => {
            const { eventName: rawName, eventType, properties } = event as ITrackEvents;
            const props = {
                ...properties,
                app_path: properties?.app_path || appPath
            };

            logEvent({ eventName: rawName, eventType, properties: props });
            if (!shouldTrackEvents) {
                return;
            }
            const eventName = `${eventType}: ${rawName}`;

            trackFullstoryEvent({
                event: eventName,
                type: eventType,
                props
            });
            trackSegmentEvent(eventName, properties);

            if (!isMixpanelLoaded) {
                delayedEvents.push({ eventName, eventType, properties: props });
                return;
            }

            if (mixpanel.get_distinct_id()) {
                mixpanel.track(eventName, {
                    ...props
                });
            } else {
                logger.error({ eventName }, 'error tracking event, mixpanel was not loaded');
            }
        },
        [logEvent, shouldTrackEvents, trackFullstoryEvent, trackSegmentEvent]
    );
}

export default function useAnalytics() {
    const { track: originalTrack } = useExternalAnalytics<typeof AnalyticsEventRecord>();
    const session = useSession();

    // This is the same implementation from hb-react
    const fireEvent = useCallback(
        (event: ITrackEvents) => {
            const { eventName, eventType, properties } = event;
            const props = {
                eventName,
                action: eventType,
                ...properties,
                userId: session.data?.user._id
            } as unknown as URLSearchParams; // typescript needs to be told that this is a URLSearchParams, URLSearchParams() is ok with this type.
            const queryString = new URLSearchParams(props).toString();

            const url = `https://etp.honeybook.com/etp.png?${queryString}`;
            const fetchParams: RequestInit = {
                method: 'get',
                mode: 'no-cors',
                cache: 'no-cache' as RequestCache
            };
            if (isProductionEnv()) {
                fetch(url, fetchParams);
            } else if (!isTesting()) {
                // eslint-disable-next-line no-console
                console.log(props);
            }
        },
        [session.data?.user._id]
    );

    const track: AnalyticsReporter<typeof AnalyticsEventRecord> = useCallback(
        event => {
            // Call fireEvent for the ET
            fireEvent(event as ITrackEvents);

            // Call the original track function from useExternalAnalytics
            originalTrack(event);
        },
        [originalTrack, fireEvent]
    );

    return useMemo(
        () => ({
            track
        }),
        [track]
    );
}
